/**
 * QUAL DATA VAI EM QUE HELPER — a fronteira que este arquivo existe para guardar. São DUAS
 * classes de campo, e cada uma tem o seu helper:
 *
 * - **Data pura, lida literalmente** (`toDDMMYYYY`): `referenceDate`/`date` do plantão,
 *   um `YYYY-MM-DD` sem hora. Passar por `new Date` o converteria pelo fuso do navegador
 *   ("12/09" virando "11/09" em UTC-3, o bug que a F1 documentou): a string é lida
 *   caractere a caractere.
 * - **Instante real, lido no fuso da ESCALA** (`toHHmm` aqui; `formatDateTimeWithOffset` /
 *   `formatDateInOffset` de `@/utils/date` para data+hora): TODO valor com hora que a API
 *   serve — `declared_checkin`/`declared_checkout`, os horários do auditor, os
 *   `expectedStart`/`expectedEnd` do plantão, o `expectedSubmissionAt` da solicitação
 *   (nasce de `calculateExpectedSubmissionAtTzAware`, um `zonedTimeToUtc` — NÃO de
 *   `station.end_hour`) e os `@CreateDateColumn` (`created_at`, `submitted_at`,
 *   `reviewed_at`). O offset da string é respeitado (o backend carimba a parede com o fuso
 *   do plantão, regra BF7) e a leitura é feita no offset da escala — nunca no do navegador
 *   nem no do servidor (decisão de produto da F2); o default é Brasília para quando a tela
 *   ainda não sabe o fuso.
 * - **Nunca** ler `HH:mm` de um ISO com hora caractere a caractere: o `Z` de um instante
 *   não é decorativo. Foi assim que o prazo de 10:00 em Brasília virou 13:00 na tela de
 *   detalhe da solicitação (achado Opus IMPORTANT-1) — o helper que fazia isso
 *   (`toWallClockLabel`) foi removido.
 *
 * Na dúvida, olhe como o backend PRODUZ o valor (`wallClockToIso`? `zonedTimeToUtc`?
 * `@CreateDateColumn`?) antes de escolher.
 */
import { format } from 'date-fns'

import { DEFAULT_UTC_OFFSET, formatHourMinuteInOffset } from '@/utils/date'

/**
 * `HH:mm` de um horário do Registro de Atividade, SEMPRE lido no fuso da ESCALA
 * (padrão Brasília quando a tela ainda não sabe o fuso do plantão) — nunca no do
 * navegador e nunca no do servidor.
 *
 * Era um leitor literal da string, e isso escondia um erro de 3 h: o backend roda
 * com `TZ=America/Sao_Paulo`, então um `declared_checkin` de parede 08:00 sai da
 * API como `2026-09-09T11:00:00.000Z`, e ler o literal `11` mostrava 11:00 na
 * fila (o check-out chegava a virar o dia). Interpretando o instante no offset do
 * plantão, os dois lados voltam a concordar — e um `HH:mm` puro, que já é hora de
 * parede, continua passando intacto.
 *
 * Mora aqui, e não dentro de um componente, porque o card da fila e a tela de
 * auditoria mostram O MESMO horário: duas implementações divergiam no primeiro
 * navegador fora de UTC-3.
 */
export function toHHmm(value?: string | null, offset: string = DEFAULT_UTC_OFFSET): string {
  if (!value) return ''
  const plain = /^(\d{2}):(\d{2})/.exec(value)
  if (plain) return `${plain[1]}:${plain[2]}`
  return formatHourMinuteInOffset(value, offset) ?? ''
}

/**
 * `dd/MM/yyyy` de uma DATA PURA (`referenceDate`/`date` do plantão, `YYYY-MM-DD` sem hora),
 * lida literalmente da string, sem `new Date`: o navegador em UTC-3 converteria "2026-09-12"
 * em "11/09". Não é para ISO com hora — esses são instantes e vão em `formatDateInOffset`.
 */
export function toDDMMYYYY(value?: string | null): string {
  if (!value) return ''
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
  const alreadyBr = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(value)
  if (alreadyBr) return alreadyBr[0]
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? '' : format(parsed, 'dd/MM/yyyy')
}
